// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 8eea7977-b5e9-449a-a456-8ba8ee7dd729

// ---------------------------------------------------------------------------
// NOTES AND SUPPORTING DOCUMENTS on an application
//
// Both live here because they are the same act from the user's side: sitting in
// a meeting with a client, going through an application, writing down what was
// said and attaching what was handed over.
//
// THE NOTES ARE CLIENT NOTES. They stay with the client and nothing carries
// them anywhere — not to the master catalogue, not to another workspace. A
// master can deliberately lift one insight up by writing the product-level
// sentence themselves, which is a separate action with its own confirmation;
// there is no automatic path, because "at DFCI, Greg found…" is exactly the
// text that must never appear on another client's screen.
//
// DOCUMENTS ARE FILES OR LINKS. A vendor's install guide is usually a URL we
// have no right to mirror, so a link-only entry is a first-class document
// rather than a degraded one. A blank version label means "common to every
// version", which is how the common-versus-versioned split is expressed
// without a second table.
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Link, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import { getConfig } from './config';
import { getIdToken } from './auth';
import HelpBubble from './HelpBubble';

const NOTE_TYPES = ['meeting', 'question', 'decision', 'general'];
const DOC_TYPES = ['install-guide', 'diagram', 'server-list', 'runbook', 'config', 'contract', 'training', 'other'];

async function api(path, options = {}) {
  const res = await fetch(`${getConfig().apiUrl}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
  });
  const text = await res.text();
  // An error envelope is often HTML rather than JSON — a gateway refusal, a WAF
  // block — and a bare JSON.parse throws before the real status can be shown.
  let body = {};
  let parseFailed = false;
  try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
  if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
  if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
  return body;
}

export default function AppNotesPanel({ factSheetId, tenantId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [noteBody, setNoteBody] = useState('');
  const [noteType, setNoteType] = useState('meeting');

  const [docOpen, setDocOpen] = useState(false);
  const [doc, setDoc] = useState({ title: '', docType: 'install-guide', versionLabel: '', externalUrl: '' });
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    if (!factSheetId || !tenantId) return;
    const qs = `factSheetId=${encodeURIComponent(factSheetId)}&tenantId=${encodeURIComponent(tenantId)}`;
    try {
      const [n, d] = await Promise.all([
        api(`/notes?${qs}`).catch(() => []),
        api(`/appdocs?${qs}`).catch(() => []),
      ]);
      setNotes(Array.isArray(n) ? n : []);
      setDocs(Array.isArray(d) ? d : []);
    } catch (e) { setErr(e.message); }
  }, [factSheetId, tenantId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setBusy(true); setErr('');
    try {
      await api('/notes', {
        method: 'POST',
        body: JSON.stringify({ tenantId, factSheetId, body: noteBody.trim(), noteType }),
      });
      setNoteBody(''); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Ticking a question off in the meeting it was asked in — the reason
  // `resolved` exists rather than a second note saying "answered".
  const toggleResolved = async (n) => {
    setBusy(true);
    try {
      await api(`/notes?id=${encodeURIComponent(n.id)}&tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'PUT', body: JSON.stringify({ tenantId, resolved: !n.resolved }),
      });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const removeNote = async (n) => {
    setBusy(true);
    try {
      await api(`/notes?id=${encodeURIComponent(n.id)}&tenantId=${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const addDoc = async () => {
    if (!doc.title.trim() || (!file && !doc.externalUrl.trim())) return;
    setBusy(true); setErr('');
    try {
      let s3Key = null;
      let contentType = null;
      let sizeBytes = null;
      if (file) {
        // Presign, then PUT straight to S3. The bytes never pass through the
        // API — a 30-second Lambda is the wrong place for a 40MB diagram.
        const pre = await api('/appdocs/presign', {
          method: 'POST',
          body: JSON.stringify({ tenantId, factSheetId, fileName: file.name, contentType: file.type }),
        });
        const put = await fetch(pre.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        s3Key = pre.key; contentType = file.type || null; sizeBytes = file.size;
      }
      await api('/appdocs', {
        method: 'POST',
        body: JSON.stringify({
          tenantId, factSheetId,
          title: doc.title.trim(), docType: doc.docType,
          versionLabel: doc.versionLabel.trim() || null,
          externalUrl: doc.externalUrl.trim() || null,
          s3Key, contentType, sizeBytes,
        }),
      });
      setDocOpen(false); setDoc({ title: '', docType: 'install-guide', versionLabel: '', externalUrl: '' });
      setFile(null); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const openDoc = async (d) => {
    if (d.externalUrl) { window.open(d.externalUrl, '_blank', 'noopener'); return; }
    try {
      const { url } = await api(`/appdocs/url?key=${encodeURIComponent(d.s3Key)}`);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setErr(e.message); }
  };

  const removeDoc = async (d) => {
    setBusy(true);
    try {
      await api(`/appdocs?id=${encodeURIComponent(d.id)}&tenantId=${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Common files first, then versioned ones — the order people read them in.
  const common = docs.filter((d) => !d.versionLabel);
  const versioned = docs.filter((d) => d.versionLabel);

  return (
    <Box>
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Client notes</Typography>
        <HelpBubble topic="clientNotes" />
        <Chip size="small" label={notes.length} />
      </Stack>

      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="flex-start">
          <TextField
            size="small" fullWidth multiline minRows={1} placeholder="What was said, asked, or decided…"
            value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
          />
          <TextField select size="small" value={noteType} onChange={(e) => setNoteType(e.target.value)} sx={{ width: 130 }}>
            {NOTE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <Button variant="contained" disabled={busy || !noteBody.trim()} onClick={addNote}>Add</Button>
        </Stack>
      )}

      {!notes.length && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          No notes yet. These stay with this client and are never copied to the master catalogue.
        </Typography>
      )}

      <Stack spacing={0.5} sx={{ mb: 3 }}>
        {notes.map((n) => (
          <Stack key={n.id} direction="row" alignItems="flex-start" spacing={1}>
            <Checkbox
              size="small" checked={!!n.resolved} disabled={!canEdit || busy}
              onChange={() => toggleResolved(n)} sx={{ mt: -0.5 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2"
                sx={{ textDecoration: n.resolved ? 'line-through' : 'none', opacity: n.resolved ? 0.6 : 1 }}>
                {n.body}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {n.noteType} · {n.author}
                {n.promoted && ' · promoted to the master catalogue'}
              </Typography>
            </Box>
            {canEdit && (
              <Tooltip title={n.promoted ? 'Promoted notes are the record of where a catalogue line came from' : 'Delete'}>
                <span>
                  <IconButton size="small" disabled={busy} onClick={() => removeNote(n)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Supporting documents</Typography>
        <HelpBubble topic="supportingDocuments" />
        <Chip size="small" label={docs.length} />
        <Box sx={{ flex: 1 }} />
        {canEdit && <Button size="small" startIcon={<AddIcon />} onClick={() => setDocOpen(true)}>Add</Button>}
      </Stack>

      {!docs.length && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No install guides, diagrams, server lists or runbooks attached yet.
        </Typography>
      )}

      {[['Common to all versions', common], ['Version-specific', versioned]].map(([label, list]) => (
        !!list.length && (
          <Box key={label} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {list.map((d) => (
                <Stack key={d.id} direction="row" alignItems="center" spacing={1}>
                  {d.externalUrl ? <LinkIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                  <Link component="button" variant="body2" underline="hover"
                    onClick={() => openDoc(d)} sx={{ textAlign: 'left' }}>
                    {d.title}
                  </Link>
                  <Chip size="small" variant="outlined" label={d.docType} />
                  {d.versionLabel && <Chip size="small" label={d.versionLabel} />}
                  <Box sx={{ flex: 1 }} />
                  {canEdit && (
                    <IconButton size="small" disabled={busy} onClick={() => removeDoc(d)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        )
      ))}

      <Dialog open={docOpen} onClose={() => !busy && setDocOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Add a supporting document <HelpBubble topic="supportingDocuments" />
        </DialogTitle>
        <DialogContent dividers>
          <TextField autoFocus fullWidth size="small" label="Title" sx={{ mb: 2 }}
            value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} />
          <TextField select fullWidth size="small" label="Kind" sx={{ mb: 2 }}
            value={doc.docType} onChange={(e) => setDoc({ ...doc, docType: e.target.value })}>
            {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField fullWidth size="small" label="Version (leave blank if it applies to every version)"
            sx={{ mb: 2 }} value={doc.versionLabel}
            onChange={(e) => setDoc({ ...doc, versionLabel: e.target.value })} />
          {/* A link and a file are alternatives, not both — a vendor page we may
              not mirror is as real a document as one we hold. */}
          <TextField fullWidth size="small" label="Link (a vendor page, SharePoint, Confluence…)"
            sx={{ mb: 2 }} value={doc.externalUrl} disabled={!!file}
            onChange={(e) => setDoc({ ...doc, externalUrl: e.target.value })} />
          <Button component="label" variant="outlined" size="small" disabled={!!doc.externalUrl.trim()}>
            {file ? file.name : 'or upload a file'}
            <input type="file" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDocOpen(false); setFile(null); }} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={addDoc}
            disabled={busy || !doc.title.trim() || (!file && !doc.externalUrl.trim())}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
